import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2, ArrowUpRight, CheckCircle2, X } from "lucide-react";

/**
 * A compact, reusable chip that opens a modal for quick access code redemption.
 * Placed in the top nav (marketing) and on the /login page.
 */
export default function AccessCodeChip({ variant = "light", testId = "access-code-chip" }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e?.preventDefault();
    const cleaned = (code || "").trim().toUpperCase();
    if (!cleaned) { toast.error("Please enter your code."); return; }
    setLoading(true);
    try {
      await api.post("/access/validate-code", { code: cleaned });
      setValidated(true);
      toast.success("Code accepted — complimentary access unlocked.");
      setTimeout(() => {
        setOpen(false);
        navigate(`/signup?code=${encodeURIComponent(cleaned)}`);
      }, 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code.");
    } finally { setLoading(false); }
  };

  const trigger = variant === "light" ? (
    <button
      data-testid={testId}
      onClick={() => setOpen(true)}
      className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800 transition-all hover:bg-emerald-100"
    >
      <KeyRound className="h-3 w-3" />
      Have a code? Working with IXL?
    </button>
  ) : (
    <button
      data-testid={testId}
      onClick={() => setOpen(true)}
      className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-800 transition-all hover:bg-emerald-100"
    >
      <KeyRound className="h-3.5 w-3.5" />
      Have a code? Working with IXL?
    </button>
  );

  return (
    <>
      {trigger}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-sm p-4" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-md rounded-lg border border-stone-200 bg-white p-8 shadow-[0_25px_80px_-15px_rgb(15,23,42,0.4)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-950 text-white">
                  <KeyRound className="h-4 w-4" />
                </div>
                <div>
                  <div className="label-mono">Complimentary Access</div>
                  <div className="mt-0.5 font-display text-lg font-medium text-slate-950">Redeem your code</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="access-code-modal-close" className="rounded-md p-1 text-stone-500 hover:bg-stone-100 hover:text-slate-950">
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={submit} className="mt-6">
              <label className="block">
                <span className="label-mono block mb-2">Your code</span>
                <input
                  autoFocus
                  data-testid="access-code-modal-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ""))}
                  placeholder="e.g. RIBBA1"
                  className="w-full rounded-md border border-stone-300 bg-white px-3 py-3 font-mono text-base uppercase tracking-[0.15em] text-slate-950 outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                  autoComplete="off"
                />
              </label>
              <button type="submit" data-testid="access-code-modal-submit" disabled={loading} className="btn-primary mt-4 w-full">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</>
                         : <>Redeem & Continue <ArrowUpRight className="h-4 w-4" /></>}
              </button>
              {validated && (
                <div className="mt-4 flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="text-emerald-800">Redirecting to sign up…</span>
                </div>
              )}
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-wider text-stone-500">
                No card required • Instant activation
              </p>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
